import type { TenantEntry, TenantRegistry } from './registry'

export interface TenantSelector {
	key?: string | null
	host?: string | null
}

/**
 * Browser Host headers and proxy values arrive with ports and arbitrary case;
 * declared deployment hosts are bare lowercase. Normalising here (not at each
 * call site) is what makes `localhost:3000` and `Example.COM:443` match.
 */
export function normalizeHost(host: string): string {
	return host.trim().toLowerCase().replace(/:\d+$/, '')
}

/**
 * Resolve a tenant from an identifier that may be an explicit key pin or a
 * real browser host. Key is tried first (exact, unique), then the host field.
 * Entries sharing a host resolve to the first declared when matched by host;
 * pinning by key is the only way to select a specific one (the Marquee AU/NZ
 * UAT pair share one host).
 */
export function resolveTenant<TSettings>(
	registry: TenantRegistry<TSettings>,
	selector: TenantSelector,
): TenantEntry<TSettings> | undefined {
	if (selector.key) {
		const hit = registry.byKey[selector.key]
		if (hit) return hit
	}
	if (selector.host) {
		const host = normalizeHost(selector.host)
		return registry.byKey[host] ?? registry.byHost(host)
	}
	return undefined
}
